import { useCallback, useEffect, useRef, useState } from "react";
import "./CatalogAutocomplete.css";

/**
 * Campo de texto con autocompletado que muestra nombres pero almacena IDs.
 *
 * @param {number|null}            value     - ID actualmente seleccionada (null = vacío)
 * @param {function(number|null)}  onChange  - Llamado con la nueva ID (o null al borrar)
 * @param {{ id: number, label: string }[]} options - Lista completa de opciones
 * @param {string}                 label     - Texto del label flotante
 */
export default function CatalogAutocomplete({ value, onChange, options = [], label }) {
  const [query, setQuery] = useState("");
  const [open, setOpen]   = useState(false);
  const containerRef = useRef(null);

  // Sincronizar el texto cuando cambia el valor o las opciones (ej. al abrir formulario con datos existentes)
  useEffect(() => {
    const opt = value != null ? options.find((o) => o.id === value) : null;
    setQuery(opt ? opt.label : "");
  }, [value, options]);

  const filtered = query.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 15)
    : options.slice(0, 15);

  function handleInput(e) {
    setQuery(e.target.value);
    setOpen(true);
    if (!e.target.value.trim()) onChange(null);
  }

  function handleSelect(opt) {
    setQuery(opt.label);
    onChange(opt.id);
    setOpen(false);
  }

  function handleClear(e) {
    e.stopPropagation();
    setQuery("");
    onChange(null);
    setOpen(false);
  }

  // Al cerrar por clic fuera: restaurar el label del valor seleccionado (o limpiar si no hay selección)
  const handleOutsideClick = useCallback(
    (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        const opt = value != null ? options.find((o) => o.id === value) : null;
        setQuery(opt ? opt.label : "");
      }
    },
    [value, options]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [handleOutsideClick]);

  const hasValue = value != null || !!query.trim();

  return (
    <div
      className={`champ-ac${open ? " champ-ac--open" : ""}${hasValue ? " champ-ac--has-value" : ""}`}
      ref={containerRef}
    >
      <div className="champ-ac-wrap">
        <input
          type="text"
          className="champ-ac-input"
          value={query}
          onChange={handleInput}
          onFocus={() => setOpen(true)}
          autoComplete="off"
          spellCheck={false}
        />
        <label className="champ-ac-label">{label}</label>
        {hasValue && (
          <button type="button" className="champ-ac-clear" tabIndex={-1} onClick={handleClear}>
            ×
          </button>
        )}
      </div>
      {open && (
        <ul className="champ-ac-list">
          {filtered.length === 0 ? (
            <li className="champ-ac-empty">Sin resultados</li>
          ) : (
            filtered.map((opt) => (
              <li
                key={opt.id}
                className={`champ-ac-item${opt.id === value ? " champ-ac-item--selected" : ""}`}
                onMouseDown={() => handleSelect(opt)}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
